import { apiRequest } from "./api.client";

// ── Types (mirror app/api/v1/endpoints/platform_ops.py + billing grant-trial) ──

export interface PlatformSubscriptionSummary {
  subscription_id: string;
  plan_id: string;
  status: string;              // e.g. "activated" | "active" | "authenticated" | "expired" | …
  is_trial_row: boolean;       // subscription_id starts with "manual_trial_"
  current_period_end: string | null;
}

export interface PlatformFeatureFlagSummary {
  feature: string;
  is_enabled: boolean;
  is_trial: boolean;
  expires_at: string | null;
}

/** One row of GET /platform/clinics. */
export interface PlatformClinic {
  clinic_id: string;
  name: string;
  created_at: string | null;
  location: string | null;
  deployment_mode: string;
  is_active: boolean;

  admin_user_id: string | null;   // null → no Admin users row (offer "Fix User Row")
  admin_email: string | null;
  admin_name: string | null;

  doctor_count: number;
  receptionist_count: number;

  subscription: PlatformSubscriptionSummary | null;
  features: PlatformFeatureFlagSummary[];
}

/** Subscription statuses the backend treats as "active" (see list_clinics filter). */
export const ACTIVE_SUB_STATUSES = ["activated", "active", "authenticated"];

export function hasActiveSubscription(c: PlatformClinic): boolean {
  return !!c.subscription && ACTIVE_SUB_STATUSES.includes(c.subscription.status);
}

/** Active AND platform-granted (manual) trial → eligible for "Extend Trial". */
export function isActiveManualTrial(c: PlatformClinic): boolean {
  return hasActiveSubscription(c) && !!c.subscription?.is_trial_row;
}

export interface ListClinicsParams {
  limit?: number;
  offset?: number;
  hasActiveTrial?: boolean;   // true = active manual trial only; false = no active sub; omit = all
}

export interface GrantTrialOptions {
  days?: number;   // default 14, range 1..90
  tier?: string;   // "premium" | "base" (default "base" — the only sold plan)
  force?: boolean; // true → supersede an active sub (extend / re-tier)
}

export interface GrantTrialResult {
  subscription_id: string;
  plan_id: string;
  status: string;
  clinic_id: string;
  current_period_end: string | null;
  granted_by: string;
  force: boolean;
}

export interface GrantCreditsResult {
  clinic_id: string;
  amount_paise: number;
  balance_paise: number;   // wallet balance after the grant
  granted_by: string;
}

/** Rs 100 in paise — the size of the included allowance, and the default top-up. */
export const DEFAULT_CREDIT_GRANT_PAISE = 10000;

/** POST /platform/users/backfill body. */
export interface BackfillUserRequest {
  email: string;
  clinic_id: string;
  role: "Doctor" | "Admin" | "Receptionist";
  first_name?: string;
  last_name?: string;
}

export interface BackfillUserResult {
  user_id: string;
  email: string;
  role: string;
  clinic_id: string;
  clinic_name: string;
  created: boolean;
  old_role: string | null;
  old_clinic_id: string | null;
  cognito_synced: boolean;
  cognito_error: string | null;
}

// ── Per-clinic document roll-up (GET /platform/clinics/{id}/documents) ──────────

export type ClinicDocStatus = "not_uploaded" | "submitted" | "verified" | "rejected";

/** One expected document for a clinic. Labels + required flags come from the backend. */
export interface ClinicDocItem {
  doc_type: string;
  label: string;
  scope: "clinic" | "doctor";
  subject_kind: "clinic" | "doctor" | "onboarding";  // picks the review verify/reject route
  required: boolean;
  status: ClinicDocStatus;
  rejection_reason: string | null;
  doc_id: string | null;
  doctor_id: string | null;
  doctor_name: string | null;
  submitted_at: string | null;
  verified_at: string | null;
  updated_at: string | null;
  view_url: string | null;
}

/** One staff member (a Doctor row) with their own KYC documents. */
export interface ClinicUserGroup {
  doctor_id: string;
  user_id: string | null;
  name: string;
  role: string;                                        // "Doctor" | "Admin" | …
  overall_status: "verified" | "rejected" | "pending" | "incomplete";
  required_verified: number;
  required_total: number;
  documents: ClinicDocItem[];
}

export interface ClinicDocumentsResponse {
  clinic_id: string;
  clinic_name: string;
  location: string | null;
  mandatory_verified: number;                          // over clinic_documents (institutional)
  mandatory_total: number;
  optional_verified: number;
  optional_total: number;
  kyc_complete: boolean;
  clinic_documents: ClinicDocItem[];                   // institutional, one row each
  users: ClinicUserGroup[];                            // per-doctor/admin KYC groups
}

// ── API ────────────────────────────────────────────────────────────────────────

export const PlatformClinicsAPI = {
  /** GET /platform/clinics — cross-clinic list for the ops table (newest first). */
  listClinics(params: ListClinicsParams = {}): Promise<PlatformClinic[]> {
    const q = new URLSearchParams();
    if (params.limit != null) q.set("limit", String(params.limit));
    if (params.offset != null) q.set("offset", String(params.offset));
    if (params.hasActiveTrial != null) q.set("has_active_trial", String(params.hasActiveTrial));
    const qs = q.toString();
    return apiRequest<PlatformClinic[]>(`/platform/clinics${qs ? `?${qs}` : ""}`);
  },

  /**
   * Grant (or, with force, extend/re-tier) a manual trial.
   * POST /platform/clinics/{id}/grant-trial?days=14&tier=base[&force=true]
   */
  grantTrial(clinicId: string, opts: GrantTrialOptions = {}): Promise<GrantTrialResult> {
    const { days = 14, tier = "base", force = false } = opts;
    const params = new URLSearchParams({ days: String(days), tier });
    if (force) params.set("force", "true");
    return apiRequest<GrantTrialResult>(
      `/platform/clinics/${clinicId}/grant-trial?${params.toString()}`,
      { method: "POST" }
    );
  },

  /**
   * Add discretionary credits to a clinic's wallet.
   * POST /platform/clinics/{id}/credits
   *
   * The included allowance is a single Rs 100 per billing period, spent across
   * comms sends and AI Scribe runs from the same wallet. This is how an operator
   * goes beyond that without selling a top-up. Not idempotent: two calls are two
   * grants, so the caller must guard against a double submit.
   */
  grantCredits(
    clinicId: string,
    amountPaise: number,
    reason: string
  ): Promise<GrantCreditsResult> {
    return apiRequest<GrantCreditsResult>(`/platform/clinics/${clinicId}/credits`, {
      method: "POST",
      body: JSON.stringify({ amount_paise: amountPaise, reason }),
    });
  },

  /**
   * Toggle a feature flag for a clinic.
   * PATCH /platform/clinics/{id}/features/{feature}
   */
  toggleFeature(clinicId: string, feature: string, isEnabled: boolean): Promise<{ clinic_id: string; feature: string; is_enabled: boolean }> {
    return apiRequest(`/platform/clinics/${clinicId}/features/${feature}`, {
      method: "PATCH",
      body: JSON.stringify({ is_enabled: isEnabled }),
    });
  },

  /**
   * Repair a missing/inconsistent users row from Cognito state ("Fix User Row").
   * POST /platform/users/backfill
   */
  backfillUser(body: BackfillUserRequest): Promise<BackfillUserResult> {
    return apiRequest<BackfillUserResult>("/platform/users/backfill", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** GET /platform/clinics/{id}/documents — full required-vs-uploaded roll-up for one clinic. */
  getClinicDocuments(clinicId: string): Promise<ClinicDocumentsResponse> {
    return apiRequest<ClinicDocumentsResponse>(`/platform/clinics/${clinicId}/documents`);
  },
};
